import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import logoMarkOnDark from '../../../assets/logo-mark-on-dark.png';
import { tokens } from '../../../design/tokens';
import type { LoginStep } from '../loginStateMachine';

/**
 * Left-pane brand panel rendered alongside every step of the login
 * wizard.
 *
 * AUT-110 rework: the wizard is now 2 steps (`login` + optional `totp`)
 * instead of 3 (`phone` + `otp` + `totp`). The progress timeline shrinks
 * to two rows; the optional `email` from `LoginState` surfaces under
 * the TOTP row so the operator sees which account the 2FA prompt is for.
 */
export type BrandPanelProps = {
  step: LoginStep;
  /** Email entered at the login step — surfaced on the TOTP step. */
  email: string;
};

type StepStatus = 'done' | 'active' | 'pending';

const stepOrder: LoginStep[] = ['login', 'totp'];

const statusFor = (current: LoginStep, target: LoginStep): StepStatus => {
  if (current === target) return 'active';
  return stepOrder.indexOf(target) < stepOrder.indexOf(current) ? 'done' : 'pending';
};

const dotColors = (status: StepStatus): { bg: string; border: string; color: string } => {
  if (status === 'active') {
    return { bg: tokens.brand.primary, border: tokens.brand.primary, color: '#fff' };
  }
  if (status === 'done') {
    return {
      bg: 'rgba(52, 199, 123, 0.16)',
      border: tokens.brand.primary,
      color: tokens.brand.primary,
    };
  }
  return {
    bg: 'rgba(255, 255, 255, 0.04)',
    border: 'rgba(255, 255, 255, 0.12)',
    color: 'rgba(255, 255, 255, 0.4)',
  };
};

const Checkmark = (): ReactElement => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type StepRowProps = {
  numeral: string;
  status: StepStatus;
  title: string;
  sub: string;
};

const StepRow = ({ numeral, status, title, sub }: StepRowProps): ReactElement => {
  const dot = dotColors(status);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', position: 'relative' }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '999px',
          background: dot.bg,
          border: `2px solid ${dot.border}`,
          color: dot.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: tokens.font.mono,
          fontWeight: 700,
          fontSize: 12,
          flexShrink: 0,
          boxShadow: status === 'active' ? '0 0 0 6px rgba(52, 199, 123, 0.16)' : 'none',
        }}
        aria-current={status === 'active' ? 'step' : undefined}
      >
        {status === 'done' ? <Checkmark /> : numeral}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: status === 'done' ? 'rgba(255, 255, 255, 0.65)' : '#fff',
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.42)', mt: '2px' }}>
          {sub}
        </Typography>
      </Box>
    </Box>
  );
};

const BrandMark = (): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
    {/*
      The Inspecto mark — the letter i whose dot is a lens (AUT-511): glyph
      only, no tile (D-7), and the bare glyph is always brand green (D-11).
      The glyph is taller than wide, so height drives the size and width follows.
    */}
    <Box
      component="img"
      src={logoMarkOnDark}
      alt=""
      data-testid="brand-glyph"
      sx={{ height: 44, width: 'auto', display: 'block' }}
    />
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.2px' }}>
        Inspecto
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 500,
          opacity: 0.5,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          mt: '2px',
        }}
      >
        Admin Console
      </Typography>
    </Box>
  </Box>
);

export const BrandPanel = ({ step, email }: BrandPanelProps): ReactElement => {
  const { t } = useTranslation();
  const loginSub = email ? email : t('auth.login.step.login');
  return (
    <Box
      component="aside"
      sx={{
        flex: '0 0 580px',
        position: 'relative',
        padding: '56px',
        color: '#fff',
        overflow: 'hidden',
        background:
          'radial-gradient(120% 80% at -10% -10%, rgba(52, 199, 123, 0.32) 0%, transparent 60%),' +
          ' radial-gradient(90% 70% at 110% 110%, rgba(15, 107, 58, 0.55) 0%, transparent 60%),' +
          ' linear-gradient(160deg, #0e1c14 0%, #07120c 100%)',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      data-testid="brand-panel"
    >
      <BrandMark />

      <Box sx={{ position: 'relative' }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1.6px',
            color: tokens.brand.primary,
            mb: '28px',
          }}
        >
          {t('auth.login.step.eyebrow')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <StepRow
            numeral="1"
            status={statusFor(step, 'login')}
            title={t('auth.login.step.login')}
            sub={loginSub}
          />
          <StepRow
            numeral="2"
            status={statusFor(step, 'totp')}
            title={t('auth.login.step.totp')}
            sub={t('auth.login.step.totpDescription')}
          />
        </Box>
      </Box>

      <Box sx={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.45)' }}>INSPECTO ADMIN · BY</Box>
    </Box>
  );
};
